/**
 * The one categorical palette for chips and avatars whose colour means "which one", not a status:
 * a task label's picked colour, a Gmail Topic label, a contact's initials. Status colours are the
 * tone tokens (Badge variant success, warning…); never use these for a status.
 *
 * The names match the task labels' stored colours (TASK_LABEL_COLORS in @shared/types/tasks), so
 * those must stay a subset of CHIP_COLOURS.
 */

export const CHIP_COLOURS = ['gray', 'red', 'orange', 'amber', 'green', 'teal', 'blue', 'violet', 'pink'] as const;

export type ChipColour = (typeof CHIP_COLOURS)[number];

// The one place outside the tone tokens that uses Tailwind's palette, on purpose: these colours mean
// "which one", not a status. (components/ui is outside ESLint's palette ban, so no disable is needed;
// pages use these maps rather than their own lists and eslint-disables.)
/** A chip's soft background and strong text. */
export const CHIP_COLOUR_CLASSES: Record<ChipColour, string> = {
  gray: 'bg-zinc-100 text-zinc-700',
  red: 'bg-red-100 text-red-800',
  orange: 'bg-orange-100 text-orange-800',
  amber: 'bg-amber-100 text-amber-800',
  green: 'bg-green-100 text-green-800',
  teal: 'bg-teal-100 text-teal-800',
  blue: 'bg-blue-100 text-blue-800',
  violet: 'bg-violet-100 text-violet-800',
  pink: 'bg-pink-100 text-pink-800',
};

/** A solid swatch of the colour, for colour pickers. */
export const CHIP_SWATCH_CLASSES: Record<ChipColour, string> = {
  gray: 'bg-zinc-400',
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  amber: 'bg-amber-400',
  green: 'bg-green-500',
  teal: 'bg-teal-500',
  blue: 'bg-blue-500',
  violet: 'bg-violet-500',
  pink: 'bg-pink-500',
};

/** The colours a name can land on: not gray (reads as "off") or red (reads as an error). */
const NAME_COLOURS: readonly ChipColour[] = ['amber', 'blue', 'green', 'violet', 'teal', 'pink', 'orange'];

/** The same colour for the same name, every time (a Topic label, a contact's address). */
export function colourForName(name: string): ChipColour {
  let hash = 0;
  for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return NAME_COLOURS[hash % NAME_COLOURS.length];
}
